import type { IButton } from '../../../types/Button/button';
import type { IDefaultDropdownMenuItem } from '../../../types/Dropdown/dropdown_menu_item/dropdownMenuItem';
import type {
  ICellInfoReturn,
  IColumnMap,
  IColumnsMap,
  TCellType,
  TDynamicCellType,
} from '../../../types/data_table';
import type {
  ITableCell,
  ITableCellBase,
} from '../../../types/table/table_row/table_cell';

import { getValueFromNestedObject as getValue } from '../../../utils/getValueFromNestedObject';

import { EMPTY_CELL_VALUE } from './cellValue';

/**
 * Step two of rendering a cell: given the displayable value produced by
 * `cellValue.ts`, work out which `TableCell` variant shows it and with what
 * props.
 */

type TColumn = IColumnsMap[number];

/**
 * A column's `type` may be a literal (`'badge'`) or a rule that picks the type
 * per row — a status column that renders as a badge for known statuses and as
 * plain text otherwise. Resolve it against the row before branching on it.
 */
export const resolveCellType = (
  column: TColumn,
  row: Record<string, any>
): TCellType | TDynamicCellType => {
  if (
    typeof column.type !== 'object' ||
    column.type.conditionsType !== 'direct_value'
  ) {
    return column.type;
  }

  return getValue(row, {
    conditionsType: 'direct_value',
    conditions: column.type.conditions,
    default: column.type.default,
  });
};

/**
 * Binds the row into each action button's click handler, so consumers can
 * declare `onClickHandler: (row) => ...` on the column and get the row back.
 */
const bindActionButtons = (
  actionBtnsProps: IColumnMap['actionBtnsProps'],
  row: Record<string, any>
): IButton[] | undefined => {
  const buttons =
    typeof actionBtnsProps === 'function'
      ? actionBtnsProps(row)
      : actionBtnsProps;

  return buttons?.map((btnProps: IButton) => ({
    ...btnProps,
    onClickHandler: btnProps.onClickHandler
      ? () => btnProps.onClickHandler(row)
      : undefined,
  }));
};

/**
 * Maps a value to a badge, looking colour and text up in the column's `label`
 * dictionary and falling back to `labelFallback` — then to the value itself —
 * when the dictionary has no entry for it.
 */
const toBadgeCellProps = (
  column: TColumn,
  row: Record<string, any>,
  value: string
): ITableCell => {
  const comparingValue = getValue(
    row,
    column.labelComparingValue || column.key
  );

  return {
    type: 'badge',
    badgeProps: {
      color:
        column.label?.[comparingValue]?.color || column.labelFallback?.color,
      text: column.label?.[value]?.text || column.labelFallback?.text || value,
    },
  };
};

/** Falls back to a plain text cell, carrying any hint text and avatar along. */
const toTextCellProps = (cellInfo: ICellInfoReturn): ITableCell => {
  if (typeof cellInfo === 'string' || typeof cellInfo === 'number') {
    return { type: 'text', text: cellInfo as string };
  }

  // A list here means a misconfigured column — only `payment_method` and
  // `dropdown` can render one, and they are handled before this point.
  if (Array.isArray(cellInfo)) {
    return { type: 'text', text: EMPTY_CELL_VALUE };
  }

  const { text, hintText, avatar } = cellInfo as ITableCellBase;

  return { type: 'text', text, hintText, avatar };
};

/**
 * Builds the props for the `TableCell` variant this column and row call for.
 *
 * @param cellType The type resolved by {@link resolveCellType}.
 * @param cellInfo The displayable value from `getCellInfo`.
 */
export const buildCellProps = (
  column: TColumn,
  row: Record<string, any>,
  rowIndex: number,
  cellType: TCellType | TDynamicCellType,
  cellInfo: ICellInfoReturn
): ITableCell => {
  switch (cellType) {
    case 'leading_icon': {
      const iconValue = getValue(row, column.key);
      const icon =
        typeof column.icon === 'function'
          ? column.icon(iconValue)
          : column.icon || row.icon;

      return { type: 'leading_icon', icon, text: cellInfo as string };
    }

    case 'action_btns':
      return {
        type: 'action_btns',
        actionBtnsProps: bindActionButtons(column.actionBtnsProps, row),
      };

    case 'badge':
      // A badge needs a scalar to look up; anything richer falls through to
      // the text cell, which knows how to render it.
      return typeof cellInfo === 'string'
        ? toBadgeCellProps(column, row, cellInfo)
        : toTextCellProps(cellInfo);

    case 'leading_text':
      return {
        type: 'leading_text',
        text: cellInfo as string,
        badgeProps:
          typeof column.badgeProps === 'function'
            ? column.badgeProps(row, rowIndex)
            : column.badgeProps,
      };

    case 'dropdown':
      return {
        type: 'dropdown',
        dropdownMenuItemsProps: (
          (cellInfo as IDefaultDropdownMenuItem[]) || []
        ).map((menuItemProps) => ({ ...menuItemProps, additionalData: row })),
      };

    default:
      return toTextCellProps(cellInfo);
  }
};
